import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimePicker, formatTime12 } from "@/components/ui/time-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as UICalendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash2, FileText, Eye, Save, Copy,
  GripVertical, Download, MoreHorizontal, Pencil, CalendarIcon,
  Church, Clock, Calendar, BookOpen, Users,
  LayoutGrid, List, CalendarCheck, History, Share2, Loader2, ChevronRight, SlidersHorizontal,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TableFilterBar from "@/components/table/TableFilterBar";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format, parseISO, startOfDay, isBefore, isAfter } from "date-fns";
import jsPDF from "jspdf";
import MemberAutocomplete from "@/components/finance/MemberAutocomplete";
import { useFreemium } from "@/hooks/useFreemium";
import { QRCodeSVG } from "qrcode.react";
import { renderToStaticMarkup } from "react-dom/server";
import { sortGeneralFirst, sortGeneralFirstByName } from "@/lib/utils";
import { applyBulletinPdfTextStyle } from "@/lib/bulletinPdfStyles";
import { useLocation, useNavigate } from "react-router-dom";

const FALLBACK_MINISTRY_LABEL = "General Ministry";
const DEFAULT_ORDER_OF_SERVICE_TITLE = "ORDER OF SERVICE";
const DEFAULT_NEXT_WEEK_HEADING = "NEXT WEEK'S ORDER OF SERVICE";
const NEXT_WEEK_HEADING_META_ID = "__next_week_heading__";

const normalizeOrderOfServiceTitle = (value: string | null | undefined) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_ORDER_OF_SERVICE_TITLE;
  if (trimmed.toLowerCase() === FALLBACK_MINISTRY_LABEL.toLowerCase()) return DEFAULT_ORDER_OF_SERVICE_TITLE;
  return trimmed;
};

const normalizeNextWeekHeading = (value: string | null | undefined) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_NEXT_WEEK_HEADING;
  return trimmed;
};

const extractNextWeekHeading = (rawItems: any): { items: BulletinItem[]; heading: string } => {
  const list = Array.isArray(rawItems) ? rawItems : [];
  const meta = list.find((item: any) => item?.id === NEXT_WEEK_HEADING_META_ID);
  const heading = normalizeNextWeekHeading(typeof meta?.label === "string" ? meta.label : null);
  const items = list.filter((item: any) => item?.id !== NEXT_WEEK_HEADING_META_ID) as BulletinItem[];
  return { items, heading };
};

const withNextWeekHeadingMeta = (items: BulletinItem[], heading: string) => {
  return [
    { id: NEXT_WEEK_HEADING_META_ID, label: normalizeNextWeekHeading(heading), person: "", time: "", type: "meta" },
    ...items,
  ] as any;
};

const DEFAULT_ANNOUNCEMENTS_HEADING = "ANNOUNCEMENTS";
const ANNOUNCEMENTS_HEADING_META_ID = "__announcements_heading__";
const DEFAULT_OFFERING_HEADING = "TITHES & OFFERING";

const normalizeOfferingHeading = (value: string | null | undefined) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_OFFERING_HEADING;
  return trimmed;
};

const normalizeAnnouncementsHeading = (value: string | null | undefined) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_ANNOUNCEMENTS_HEADING;
  return trimmed;
};

const extractAnnouncementsHeading = (raw: any): { announcements: Announcement[]; heading: string } => {
  const list = Array.isArray(raw) ? raw : [];
  const meta = list.find((a: any) => a?.id === ANNOUNCEMENTS_HEADING_META_ID);
  const heading = normalizeAnnouncementsHeading(typeof meta?.text === "string" ? meta.text : null);
  const announcements = list.filter((a: any) => a?.id !== ANNOUNCEMENTS_HEADING_META_ID) as Announcement[];
  return { announcements, heading };
};

const withAnnouncementsHeadingMeta = (announcements: Announcement[], heading: string) => {
  return [
    { id: ANNOUNCEMENTS_HEADING_META_ID, text: normalizeAnnouncementsHeading(heading) },
    ...announcements.filter((a) => a.text.trim()),
  ] as any;
};

type PageSize = "letter" | "half-letter";
type PageLayout = "2-page" | "4-page";

interface BulletinItem {
  id: string;
  label: string;
  person: string;
  personTitle: string;
  time: string;
  type: "item" | "header" | "divider";
}

interface Announcement {
  id: string;
  text: string;
}

interface BulletinTemplate {
  id: string;
  church_id: string;
  event_id: string | null;
  is_template: boolean;
  template_name: string | null;
  name: string;
  service_type: string;
  service_date: string | null;
  service_time: string | null;
  offering_date: string | null;
  offering_snapshot: any | null;
  bulletin_items: BulletinItem[];
  next_week_items: BulletinItem[];
  next_week_heading?: string;
  announcements: Announcement[];
  announcements_heading?: string;
  notes: string | null;
  created_at: string;
}

interface ParticipantCredit {
  name: string;
  role: string;
  serviceDate: string;
  serviceType: string;
  bulletinId: string;
  bulletinName: string;
}

const DEFAULT_ITEMS: BulletinItem[] = [
  { id: "1", label: "Welcome & Opening Prayer", person: "", personTitle: "", time: "", type: "item" },
  { id: "2", label: "Worship & Praise", person: "", personTitle: "", time: "", type: "item" },
  { id: "3", label: "Scripture Reading", person: "", personTitle: "", time: "", type: "item" },
  { id: "4", label: "Tithes & Offering", person: "", personTitle: "", time: "", type: "item" },
  { id: "5", label: "Special Music / Hymn", person: "", personTitle: "", time: "", type: "item" },
  { id: "6", label: "Sermon", person: "", personTitle: "", time: "", type: "item" },
  { id: "7", label: "Altar Call", person: "", personTitle: "", time: "", type: "item" },
  { id: "8", label: "Announcements", person: "", personTitle: "", time: "", type: "item" },
  { id: "9", label: "Closing Prayer & Benediction", person: "", personTitle: "", time: "", type: "item" },
];

const formatPersonWithTitle = (item: BulletinItem) => {
  const person = (item.person || "").trim();
  if (!person) return "";
  const title = (item.personTitle || "").trim();
  return title ? `${title} ${person}` : person;
};

const fitChurchNameSize = (name: string, baseFontSize: number) => {
  const maxSize = baseFontSize * 1.45;
  const minSize = Math.max(baseFontSize * 0.78, 8);
  const targetChars = 30;
  const fitted = maxSize * Math.min(1, targetChars / Math.max(name.length, 1));
  return Math.max(minSize, fitted);
};

function uid() { return crypto.randomUUID(); }

function SortableBulletinRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 group">
      <button type="button" {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none">
        <GripVertical className="w-4 h-4" />
      </button>
      {children}
    </div>
  );
}

export default function BulletinsPage() {
  const { profile } = useChurch();
  const { user } = useAuth();
  const { isPreview, requireSubscription } = useFreemium();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const churchId = profile?.id;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [templateNameDialogOpen, setTemplateNameDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<BulletinTemplate | null>(null);
  const [search, setSearch] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantRoleFilter, setParticipantRoleFilter] = useState("all");
  const [participantDateStart, setParticipantDateStart] = useState<Date | undefined>(undefined);
  const [participantDateEnd, setParticipantDateEnd] = useState<Date | undefined>(undefined);
  const [participantViewMode, setParticipantViewMode] = useState<"table" | "list">("table");
  const [viewMode, setViewMode] = useState<"cards" | "list">("list");
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [dateRangeStart, setDateRangeStart] = useState<Date | undefined>(undefined);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | undefined>(undefined);
  const [bulletinTab, setBulletinTab] = useState("upcoming");

  // Page size & layout
  const [pageSize, setPageSize] = useState<PageSize>("letter");
  const [pageLayout, setPageLayout] = useState<PageLayout>("4-page");
  const [fontSize, setFontSize] = useState(11);
  const [previewDownloading, setPreviewDownloading] = useState(false);
  const [previewSharing, setPreviewSharing] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [serviceType, setServiceType] = useState(DEFAULT_ORDER_OF_SERVICE_TITLE);
  const [serviceDate, setServiceDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [serviceTime, setServiceTime] = useState("09:00");
  const [serviceEndTime, setServiceEndTime] = useState("10:00");
  const [items, setItems] = useState<BulletinItem[]>(DEFAULT_ITEMS);
  const [nextWeekItems, setNextWeekItems] = useState<BulletinItem[]>(DEFAULT_ITEMS);
  const [nextWeekHeading, setNextWeekHeading] = useState(DEFAULT_NEXT_WEEK_HEADING);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsHeading, setAnnouncementsHeading] = useState(DEFAULT_ANNOUNCEMENTS_HEADING);
  const [offeringHeading, setOfferingHeading] = useState(DEFAULT_OFFERING_HEADING);
  const [notes, setNotes] = useState("");
  const [linkedEventId, setLinkedEventId] = useState<string>("none");
  const [offeringDate, setOfferingDate] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 7 : day;
    d.setDate(d.getDate() - diff);
    return format(d, "yyyy-MM-dd");
  });
  const [showTitheContributorNames, setShowTitheContributorNames] = useState(true);
  const [offeringNotes, setOfferingNotes] = useState("");
  const [showYearlyBudgetComparison, setShowYearlyBudgetComparison] = useState(false);
  const offeringYear = useMemo(
    () => (offeringDate ? Number(offeringDate.slice(0, 4)) : new Date().getFullYear()),
    [offeringDate]
  );

  // Fetch tithes & offering data with individual names
  const { data: rawOfferingData } = useQuery({
    queryKey: ["offering_data", churchId, offeringDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("category, amount, payment_method, member_name, member_id")
        .eq("church_id", churchId!)
        .eq("date", offeringDate);
      if (error) throw error;
      const totals: Record<string, number> = {};
      let grand = 0;
      const titheContributions: { member_name: string | null; member_id: string | null; amount: number }[] = [];
      (data || []).forEach((r) => {
        totals[r.category] = (totals[r.category] || 0) + Number(r.amount);
        grand += Number(r.amount);
        if (r.category?.toLowerCase().includes("tithe") && (r.member_name || r.member_id)) {
          titheContributions.push({ member_name: r.member_name, member_id: r.member_id, amount: Number(r.amount) });
        }
      });
      return {
        entries: Object.entries(totals).map(([cat, amt]) => ({ category: cat, amount: amt })),
        total: grand,
        titheContributions,
      };
    },
    enabled: !!churchId && !!offeringDate,
  });

  const { data: yearlyBudgetComparison } = useQuery({
    queryKey: ["offering_budget_comparison", churchId, offeringYear],
    queryFn: async () => {
      const [{ data: budgets, error: budgetsError }, { data: records, error: recordsError }] = await Promise.all([
        supabase
          .from("annual_budgets")
          .select("jan,feb,mar,apr,may,jun,jul,aug,sep,oct,nov,dec")
          .eq("church_id", churchId!)
          .eq("year", offeringYear)
          .eq("record_type", "income"),
        supabase
          .from("finance_records")
          .select("amount")
          .eq("church_id", churchId!)
          .eq("record_type", "income")
          .gte("date", `${offeringYear}-01-01`)
          .lte("date", `${offeringYear}-12-31`),
      ]);

      if (budgetsError) throw budgetsError;
      if (recordsError) throw recordsError;

      const budgetTotal = (budgets || []).reduce((sum: number, row: any) => {
        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        return sum + months.reduce((mSum, month) => mSum + Number(row?.[month] || 0), 0);
      }, 0);
      const actualTotal = (records || []).reduce((sum: number, row: any) => sum + Number(row.amount || 0), 0);
      const percent = budgetTotal > 0 ? (actualTotal / budgetTotal) * 100 : null;

      return {
        year: offeringYear,
        budgetTotal,
        actualTotal,
        percent,
      };
    },
    enabled: !!churchId && Number.isFinite(offeringYear),
  });

  // Fetch members for person autocomplete
  const { data: members = [] } = useQuery({
    queryKey: ["members_list_bulletin", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name")
        .eq("church_id", churchId!)
        .order("full_name");
      if (error) throw error;
      return data;
    },
    enabled: !!churchId,
  });

  const { data: bulletinEvents = [] } = useQuery({
    queryKey: ["events", "bulletins", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_date, start_time")
        .eq("church_id", churchId!)
        .order("event_date", { ascending: true });
      if (error) throw error;
      return (data || []) as { id: string; name: string; event_date: string; start_time: string | null }[];
    },
    enabled: !!churchId,
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ["ministries", "bulletins", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", churchId!)
        .eq("type", "ministry")
        .order("name", { ascending: true });
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as { id: string; name: string }[]);
    },
    enabled: !!churchId,
  });

  const ministryOptions = useMemo(() => {
    const names = ministries.map((ministry) => ministry.name).filter(Boolean);
    const sortedNames = sortGeneralFirst(names);
    if (serviceType && !names.includes(serviceType)) {
      return sortGeneralFirst([serviceType, ...sortedNames]);
    }
    return sortedNames;
  }, [ministries, serviceType]);

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((member: any) => {
      if (member.id) map.set(member.id, member.full_name || "");
    });
    return map;
  }, [members]);

  const offeringData = useMemo(() => {
    if (!rawOfferingData) return null;

    const titheContributions = rawOfferingData.titheContributions
      .map((contribution) => {
        const linkedName = contribution.member_id
          ? memberNameById.get(contribution.member_id)
          : null;
        return {
          name: linkedName || contribution.member_name || "Unknown member",
          amount: contribution.amount,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const titheEntries = rawOfferingData.entries.filter((e: any) => e.category.toLowerCase().includes("tithe"));
    const offeringEntries = rawOfferingData.entries.filter((e: any) => !e.category.toLowerCase().includes("tithe"));
    const tithesTotal = titheEntries.reduce((sum: number, e: any) => sum + e.amount, 0);

    return {
      ...rawOfferingData,
      titheContributions,
      titheEntries,
      offeringEntries,
      tithesTotal,
    };
  }, [rawOfferingData, memberNameById]);

  // Fetch saved bulletins (templates are managed on a dedicated page)
  const { data: bulletins = [], isLoading } = useQuery({
    queryKey: ["bulletin_templates", churchId, "bulletins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("*")
        .eq("church_id", churchId!)
        .eq("is_template", false)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((t: any) => ({
        ...t,
        bulletin_items: (Array.isArray(t.bulletin_items) ? t.bulletin_items : []) as BulletinItem[],
        ...(() => {
          const parsedNextWeek = extractNextWeekHeading(t.next_week_items);
          return {
            next_week_items: parsedNextWeek.items,
            next_week_heading: parsedNextWeek.heading,
          };
        })(),
        ...(() => {
          const parsedAnnouncements = extractAnnouncementsHeading(t.announcements);
          return {
            announcements: parsedAnnouncements.announcements,
            announcements_heading: parsedAnnouncements.heading,
          };
        })(),
      })) as BulletinTemplate[];
    },
    enabled: !!churchId,
  });

  // Fetch saved templates
  const { data: savedTemplates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["bulletin_templates", churchId, "templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("*")
        .eq("church_id", churchId!)
        .eq("is_template", true)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((t: any) => ({
        ...t,
        bulletin_items: (Array.isArray(t.bulletin_items) ? t.bulletin_items : []) as BulletinItem[],
        ...(() => {
          const parsedNextWeek = extractNextWeekHeading(t.next_week_items);
          return {
            next_week_items: parsedNextWeek.items,
            next_week_heading: parsedNextWeek.heading,
          };
        })(),
        ...(() => {
          const parsedAnnouncements = extractAnnouncementsHeading(t.announcements);
          return {
            announcements: parsedAnnouncements.announcements,
            announcements_heading: parsedAnnouncements.heading,
          };
        })(),
      })) as BulletinTemplate[];
    },
    enabled: !!churchId,
  });

  const filteredTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return savedTemplates;
    return savedTemplates.filter((t) =>
      (t.template_name || t.name || "").toLowerCase().includes(query)
      || t.name.toLowerCase().includes(query)
      || t.service_type.toLowerCase().includes(query)
    );
  }, [savedTemplates, templateSearch]);

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bulletin_templates")
        .delete()
        .eq("id", id)
        .eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      toast.success("Template deleted");
      setDeleteTemplateId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resetForm = () => {
    setName("");
    setTemplateName("");
    setServiceType(DEFAULT_ORDER_OF_SERVICE_TITLE);
    setServiceDate(format(new Date(), "yyyy-MM-dd"));
    setServiceTime("09:00");
    setServiceEndTime("10:00");    setItems(DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
    setNextWeekItems(DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
    setNextWeekHeading(DEFAULT_NEXT_WEEK_HEADING);
    setAnnouncements([{ id: uid(), text: "" }]);
    setAnnouncementsHeading(DEFAULT_ANNOUNCEMENTS_HEADING);
    setNotes("");
    setLinkedEventId("none");
    setShowTitheContributorNames(true);
    setOfferingNotes("");
    setOfferingHeading(DEFAULT_OFFERING_HEADING);
    setShowYearlyBudgetComparison(false);
    setEditingTemplate(null);
  };

  const openTemplateAsNewDraft = useCallback((template: BulletinTemplate) => {
    if (!requireSubscription()) return;

    const today = format(new Date(), "yyyy-MM-dd");
    const templateDisplayName = template.template_name || template.name || "Bulletin";

    const parsedNextWeek = typeof template.next_week_heading === "string"
      ? {
          items: Array.isArray(template.next_week_items) ? template.next_week_items : [],
          heading: normalizeNextWeekHeading(template.next_week_heading),
        }
      : extractNextWeekHeading(template.next_week_items);

    const parsedAnnouncements = typeof template.announcements_heading === "string"
      ? {
          announcements: Array.isArray(template.announcements) ? template.announcements : [],
          heading: normalizeAnnouncementsHeading(template.announcements_heading),
        }
      : extractAnnouncementsHeading(template.announcements);

    setEditingTemplate(null);
    setName(`${templateDisplayName} - ${today}`);
    setTemplateName(template.template_name || template.name || "");
    setServiceType(normalizeOrderOfServiceTitle(template.service_type));
    setServiceDate(today);
    setServiceTime(template.service_time?.split(" - ")[0] || "09:00");
    setServiceEndTime(template.service_time?.split(" - ")[1] || "10:00");
    setItems(template.bulletin_items.length > 0 ? template.bulletin_items.map((item) => ({ ...item, id: uid() })) : DEFAULT_ITEMS.map((item) => ({ ...item, id: uid() })));
    setNextWeekItems(parsedNextWeek.items.length > 0 ? parsedNextWeek.items.map((item) => ({ ...item, id: uid() })) : DEFAULT_ITEMS.map((item) => ({ ...item, id: uid() })));
    setNextWeekHeading(parsedNextWeek.heading);
    setAnnouncements(
      parsedAnnouncements.announcements.length > 0
        ? parsedAnnouncements.announcements.map((announcement) => ({ ...announcement, id: uid() }))
        : [{ id: uid(), text: "" }],
    );
    setAnnouncementsHeading(parsedAnnouncements.heading);
    setNotes(template.notes || "");
    setLinkedEventId("none");
    setOfferingDate(template.offering_date || today);
    setShowTitheContributorNames(template.offering_snapshot?.show_tithe_contributor_names ?? true);
    setOfferingNotes(template.offering_snapshot?.offering_notes || "");
    setOfferingHeading(normalizeOfferingHeading(template.offering_snapshot?.offering_heading));
    setShowYearlyBudgetComparison(template.offering_snapshot?.show_yearly_budget_comparison ?? false);
    setBulletinTab("upcoming");
    setEditorOpen(true);
  }, [requireSubscription]);

  useEffect(() => {
    const state = location.state as { prefillTemplate?: BulletinTemplate } | null;
    if (!state?.prefillTemplate) return;

    openTemplateAsNewDraft(state.prefillTemplate);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, openTemplateAsNewDraft]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (!tab) return;
    if (["upcoming", "past", "templates", "participants"].includes(tab)) {
      setBulletinTab(tab);
    }
  }, [location.search]);

  const openEditor = (template?: BulletinTemplate) => {
    if (!requireSubscription()) return;
    if (template) {
      const parsedNextWeek = typeof template.next_week_heading === "string"
        ? {
            items: Array.isArray(template.next_week_items) ? template.next_week_items : [],
            heading: normalizeNextWeekHeading(template.next_week_heading),
          }
        : extractNextWeekHeading(template.next_week_items);
      const parsedAnnouncements = typeof template.announcements_heading === "string"
        ? {
            announcements: Array.isArray(template.announcements) ? template.announcements : [],
            heading: normalizeAnnouncementsHeading(template.announcements_heading),
          }
        : extractAnnouncementsHeading(template.announcements);
      setEditingTemplate(template);
      setName(template.name);
      setTemplateName(template.template_name || template.name || "");
      setServiceType(normalizeOrderOfServiceTitle(template.service_type));
      setServiceDate(template.service_date || format(new Date(), "yyyy-MM-dd"));
      setServiceTime(template.service_time?.split(" - ")[0] || "09:00");
      setServiceEndTime(template.service_time?.split(" - ")[1] || "10:00");
      setItems(template.bulletin_items.length > 0 ? template.bulletin_items : DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
      setNextWeekItems(parsedNextWeek.items.length > 0 ? parsedNextWeek.items : DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
      setNextWeekHeading(parsedNextWeek.heading);
      setAnnouncements(parsedAnnouncements.announcements.length > 0 ? parsedAnnouncements.announcements : [{ id: uid(), text: "" }]);
      setAnnouncementsHeading(parsedAnnouncements.heading);
      setNotes(template.notes || "");
      setLinkedEventId(template.event_id || "none");
      setOfferingDate(template.offering_date || template.service_date || format(new Date(), "yyyy-MM-dd"));
      setShowTitheContributorNames(template.offering_snapshot?.show_tithe_contributor_names ?? true);
      setOfferingNotes(template.offering_snapshot?.offering_notes || "");
      setOfferingHeading(normalizeOfferingHeading(template.offering_snapshot?.offering_heading));
      setShowYearlyBudgetComparison(template.offering_snapshot?.show_yearly_budget_comparison ?? false);
    } else {
      resetForm();
      setItems(DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
      setNextWeekItems(DEFAULT_ITEMS.map(i => ({ ...i, id: uid() })));
    }
    setEditorOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const basePayload = {
        church_id: churchId!,
        event_id: linkedEventId === "none" ? null : linkedEventId,
        is_template: false,
        template_name: null,
        name: name.trim() || `${normalizeOrderOfServiceTitle(serviceType)} - ${serviceDate}`,
        service_type: normalizeOrderOfServiceTitle(serviceType),
        service_date: serviceDate || null,
        service_time: [serviceTime, serviceEndTime].filter(Boolean).join(" - ") || null,
        offering_date: offeringDate || null,
        offering_snapshot: offeringData
          ? {
              ...JSON.parse(JSON.stringify(offeringData)),
              show_tithe_contributor_names: showTitheContributorNames,
              offering_heading: normalizeOfferingHeading(offeringHeading),
              offering_notes: offeringNotes.trim() || null,
              show_yearly_budget_comparison: showYearlyBudgetComparison,
            }
          : {
              show_tithe_contributor_names: showTitheContributorNames,
              offering_heading: normalizeOfferingHeading(offeringHeading),
              offering_notes: offeringNotes.trim() || null,
              show_yearly_budget_comparison: showYearlyBudgetComparison,
            },
        bulletin_items: items as any,
        next_week_items: withNextWeekHeadingMeta(nextWeekItems, nextWeekHeading),
        announcements: withAnnouncementsHeadingMeta(announcements, announcementsHeading),
        notes: notes.trim() || null,
      };
      if (editingTemplate) {
        const { data, error } = await supabase
          .from("bulletin_templates")
          .update(basePayload)
          .eq("id", editingTemplate.id)
          .select("id")
          .maybeSingle();
        if (error) throw error;

        // RLS can yield a successful request with zero updated rows.
        if (!data?.id) {
          throw new Error("Unable to update bulletin. You may not have permission to edit this bulletin.");
        }
      } else {
        const createPayload = {
          ...basePayload,
          created_by: user?.id || null,
        };
        const { error } = await supabase.from("bulletin_templates").insert(createPayload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      toast.success(editingTemplate ? "Bulletin updated" : "Bulletin saved");
      setEditorOpen(false);
      resetForm();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const saveAsTemplateMutation = useMutation({
    mutationFn: async () => {
      const trimmedTemplateName = templateName.trim();
      if (!trimmedTemplateName) {
        throw new Error("Template name is required.");
      }

      const templatePayload = {
        church_id: churchId!,
        event_id: null,
        is_template: true,
        template_name: trimmedTemplateName,
        name: trimmedTemplateName,
        service_type: normalizeOrderOfServiceTitle(serviceType),
        service_date: null,
        service_time: [serviceTime, serviceEndTime].filter(Boolean).join(" - ") || null,
        offering_date: offeringDate || null,
        offering_snapshot: offeringData
          ? {
              ...JSON.parse(JSON.stringify(offeringData)),
              show_tithe_contributor_names: showTitheContributorNames,
              offering_heading: normalizeOfferingHeading(offeringHeading),
              offering_notes: offeringNotes.trim() || null,
              show_yearly_budget_comparison: showYearlyBudgetComparison,
            }
          : {
              show_tithe_contributor_names: showTitheContributorNames,
              offering_heading: normalizeOfferingHeading(offeringHeading),
              offering_notes: offeringNotes.trim() || null,
              show_yearly_budget_comparison: showYearlyBudgetComparison,
            },
        bulletin_items: items as any,
        next_week_items: withNextWeekHeadingMeta(nextWeekItems, nextWeekHeading),
        announcements: withAnnouncementsHeadingMeta(announcements, announcementsHeading),
        notes: notes.trim() || null,
        created_by: user?.id || null,
      };

      const { error } = await supabase.from("bulletin_templates").insert(templatePayload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      toast.success("Template saved");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("bulletin_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      toast.success("Bulletin deleted");
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const duplicateTemplate = (t: BulletinTemplate) => {
    openEditor({
      ...t,
      id: "",
      event_id: t.event_id,
      name: `${t.name} (Copy)`,
      bulletin_items: t.bulletin_items.map(i => ({ ...i, id: uid() })),
      announcements: t.announcements.map(a => ({ ...a, id: uid() })),
    });
    setEditingTemplate(null);
  };

  // Item management
  const addItem = (type: BulletinItem["type"] = "item") => {
    setItems(prev => [...prev, { id: uid(), label: type === "header" ? "Section Header" : type === "divider" ? "" : "", person: "", personTitle: "", time: "", type }]);
  };
  const removeItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id: string, field: keyof BulletinItem, value: string) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };
  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems(prev => {
        const oldIndex = prev.findIndex(i => i.id === active.id);
        const newIndex = prev.findIndex(i => i.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // Next week item management
  const addNextWeekItem = (type: BulletinItem["type"] = "item") => {
    setNextWeekItems(prev => [...prev, { id: uid(), label: type === "header" ? "Section Header" : type === "divider" ? "" : "", person: "", personTitle: "", time: "", type }]);
  };
  const removeNextWeekItem = (id: string) => setNextWeekItems(prev => prev.filter(i => i.id !== id));
  const updateNextWeekItem = (id: string, field: keyof BulletinItem, value: string) => {
    setNextWeekItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };
  const handleNextWeekDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setNextWeekItems(prev => {
        const oldIndex = prev.findIndex(i => i.id === active.id);
        const newIndex = prev.findIndex(i => i.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // Announcement management
  const addAnnouncement = () => setAnnouncements(prev => [...prev, { id: uid(), text: "" }]);
  const removeAnnouncement = (id: string) => setAnnouncements(prev => prev.filter(a => a.id !== id));
  const updateAnnouncement = (id: string, text: string) => {
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, text } : a));
  };
  const handleAnnouncementDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setAnnouncements(prev => {
        const oldIndex = prev.findIndex(a => a.id === active.id);
        const newIndex = prev.findIndex(a => a.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // Preview template
  const [previewTemplate, setPreviewTemplate] = useState<BulletinTemplate | null>(null);
  const openPreview = (t?: BulletinTemplate) => {
    if (t) {
      setPreviewTemplate(t);
      setOfferingDate(t.offering_date || t.service_date || offeringDate);
      setShowTitheContributorNames(t.offering_snapshot?.show_tithe_contributor_names ?? true);
      setOfferingNotes(t.offering_snapshot?.offering_notes || "");
      setOfferingHeading(normalizeOfferingHeading(t.offering_snapshot?.offering_heading));
      setShowYearlyBudgetComparison(t.offering_snapshot?.show_yearly_budget_comparison ?? false);
    } else {
      setPreviewTemplate({
        id: "", church_id: churchId || "", event_id: linkedEventId === "none" ? null : linkedEventId, is_template: false, template_name: null, name, service_type: normalizeOrderOfServiceTitle(serviceType),
        service_date: serviceDate, service_time: [serviceTime, serviceEndTime].filter(Boolean).join(" - "), offering_date: offeringDate || null,
        offering_snapshot: {
          show_tithe_contributor_names: showTitheContributorNames,
          offering_notes: offeringNotes.trim() || null,
          show_yearly_budget_comparison: showYearlyBudgetComparison,
        },
        bulletin_items: items, next_week_items: nextWeekItems, next_week_heading: normalizeNextWeekHeading(nextWeekHeading), announcements: announcements.filter((a) => a.text.trim()), announcements_heading: normalizeAnnouncementsHeading(announcementsHeading), notes, created_at: new Date().toISOString(),
      });
    }
    setPreviewOpen(true);
  };

  // ============ PDF Export ============
  // Helper: load image as base64 for jsPDF with dimensions
  const loadImageAsBase64 = (url: string): Promise<{ data: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          resolve({ data: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateQrAsBase64 = (value: string): Promise<{ data: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
      try {
        const svgMarkup = renderToStaticMarkup(
          <QRCodeSVG value={value} size={512} level="H" includeMargin />
        );
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.onload = () => {
          try {
            ctx?.fillRect(0, 0, 512, 512);
            ctx!.fillStyle = "#ffffff";
            ctx!.fillRect(0, 0, 512, 512);
            ctx?.drawImage(img, 0, 0, 512, 512);
            resolve({ data: canvas.toDataURL("image/png"), width: 512, height: 512 });
          } catch {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgMarkup)))}`;
      } catch {
        resolve(null);
      }
    });
  };

  // ============ PDF Export ============
  const shareOrMailto = async (doc: jsPDF, fileName: string, title: string, bodyText: string) => {
    const blob = doc.output("blob");
    if (navigator.share && navigator.canShare) {
      const file = new File([blob], fileName, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title, files: [file] });
        return;
      }
    }
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(bodyText);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
  };

  const generatePDF = async (
    t: BulletinTemplate,
    options?: { fontSize?: number; pageLayout?: PageLayout }
  ) => {
    const activeFontSize = options?.fontSize ?? fontSize;
    const activeLayout = options?.pageLayout ?? pageLayout;
    const logoInfo = profile?.logo_url ? await loadImageAsBase64(profile.logo_url) : null;
    const checkinUrl = t.event_id ? `${window.location.origin}/checkin?event_id=${t.event_id}` : null;
    const qrInfo = checkinUrl ? await generateQrAsBase64(checkinUrl) : null;
    const linkedEvent = bulletinEvents.find((event) => event.id === t.event_id);
    
    // Always use landscape Letter format for folded bulletins
    const doc = new jsPDF({ unit: "mm", format: "letter", orientation: "landscape" });
    const fullW = doc.internal.pageSize.getWidth();
    const fullH = doc.internal.pageSize.getHeight();
    const halfW = fullW / 2;
    // Panel margins: 0.35" = 8.89mm -> use 7mm side, 9mm top/bottom
    const margin = 7;
    const topMargin = 9;

    if (activeLayout === "4-page") {
      // 4-page layout: Page 1 (front right) and Page 4 (inside left) on first sheet
      //                Page 3 (inside right) and Page 2 (back left) on second sheet (when flipped over)
      // For printing: Print first page, flip horizontally, print second page
      
      // First physical page (Page 1 right, Page 4 left)
      renderPDFPage1(doc, t, halfW + margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, activeFontSize);
      renderPDFPage4(doc, t, margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, qrInfo, checkinUrl, linkedEvent?.name || null, activeFontSize);
      
      // Draw fold line
      doc.setDrawColor(200);
      const dashLen = 3;
      for (let dy = 5; dy < fullH - 5; dy += dashLen * 2) {
        doc.line(halfW, dy, halfW, Math.min(dy + dashLen, fullH - 5));
      }
      
      // Add second physical page
      doc.addPage();
      renderPDFPage3(doc, t, halfW + margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, activeFontSize);
      renderPDFPage2(doc, t, margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, activeFontSize, activeLayout);
      
      // Draw fold line on second page
      for (let dy = 5; dy < fullH - 5; dy += dashLen * 2) {
        doc.line(halfW, dy, halfW, Math.min(dy + dashLen, fullH - 5));
      }
    } else {
      // 2-page layout: Page 1 (right) and Page 2 (left) on one sheet
      renderPDFPage1(doc, t, halfW + margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, activeFontSize);
      renderPDFPage2(doc, t, margin, topMargin, halfW - margin * 2, fullH - topMargin * 2, logoInfo, activeFontSize, activeLayout);
      
      // Draw fold line
      doc.setDrawColor(200);
      const dashLen = 3;
      for (let dy = 5; dy < fullH - 5; dy += dashLen * 2) {
        doc.line(halfW, dy, halfW, Math.min(dy + dashLen, fullH - 5));
      }
    }

    return doc;
  };

  const exportPDF = async (t: BulletinTemplate, options?: { fontSize?: number; pageLayout?: PageLayout }) => {
    const doc = await generatePDF(t, options);
    doc.save(`${t.name || "bulletin"}.pdf`);
    toast.success("PDF exported!");
  };

  const sharePDF = async (t: BulletinTemplate, options?: { fontSize?: number; pageLayout?: PageLayout }) => {
    const fileName = `${t.name || "bulletin"}.pdf`;
    const title = `${t.name || "Church Bulletin"}`;
    const body = `Please find attached bulletin: ${t.name || "Church Bulletin"}.`;
    const doc = await generatePDF(t, options);
    await shareOrMailto(doc, fileName, title, body);
  };

  // Render Page 1: Order of Service
  const renderPDFPage1 = (doc: jsPDF, t: BulletinTemplate, x: number, startY: number, w: number, h: number, logoInfo: { data: string; width: number; height: number } | null, activeFontSize: number) => {
    let y = startY;
    const pad = 5;
    const contentWidth = w - pad * 2;
    const s = activeFontSize / 10; // scale factor for spacing
    const personColumnX = x + pad + contentWidth * (2 / 3) - 4.6 * s;

    const headerTop = y;
    let logoBottom = headerTop;

    let logoSlotWidth = 0;
    if (logoInfo) {
      const maxSize = 16.93;
      const aspectRatio = logoInfo.width / logoInfo.height;
      let logoWidth = maxSize;
      let logoHeight = maxSize;

      if (aspectRatio > 1) {
        logoHeight = maxSize / aspectRatio;
      } else {
        logoWidth = maxSize * aspectRatio;
      }

      const logoX = x + pad;
      const logoY = headerTop + 1;
      try {
        doc.addImage(logoInfo.data, "PNG", logoX, logoY, logoWidth, logoHeight);
      } catch {}
      logoBottom = logoY + logoHeight;
      logoSlotWidth = maxSize + 4;
    }

    const textAreaX = x + pad + logoSlotWidth;
    const textAreaW = w - (pad * 2) - logoSlotWidth;

    let textY = headerTop + 4;

    const churchName = profile?.church_name || "Church";
    const churchNameText = churchName.toUpperCase();
    let churchNameFontSize = activeFontSize * 1.45;
    const minChurchNameFontSize = Math.max(activeFontSize * 0.78, 8);
    applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: churchNameFontSize, color: [15, 55, 103] });
    while (doc.getTextWidth(churchNameText) > Math.max(40, textAreaW) && churchNameFontSize > minChurchNameFontSize) {
      churchNameFontSize -= 0.25;
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: churchNameFontSize, color: [15, 55, 103] });
    }
    doc.text(churchNameText, textAreaX, textY);
    textY += 5.4 * s;

    if (profile?.bible_verse) {
      const verseLine = profile?.bible_reference
        ? `\"${profile.bible_verse}\" (${profile.bible_reference})`
        : `\"${profile.bible_verse}\"`;
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize * 0.95, color: [31, 41, 55] });
      const verseLines = doc.splitTextToSize(verseLine, Math.max(40, textAreaW));
      doc.text(verseLines, textAreaX, textY);
      textY += verseLines.length * (4.6 * s);
    }

    const addressLine = [profile?.address, profile?.city, profile?.state, profile?.zip_code].filter(Boolean).join(", ");
    if (addressLine) {
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 1.05, color: [17, 24, 39] });
      const addressLines = doc.splitTextToSize(addressLine, Math.max(40, textAreaW));
      doc.text(addressLines, textAreaX, textY);
      textY += addressLines.length * (4.8 * s);
    }

    y = Math.max(logoBottom, textY) + 2;

    // Date and time - base size, muted gray
    if (t.service_date || t.service_time) {
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [107, 114, 128] });
      const dateStr = t.service_date ? format(new Date(t.service_date + "T00:00"), "MMMM d, yyyy") : "";
      const timeStr = t.service_time
        ? t.service_time.split(" - ").map((t: string) => { const p = t.trim().split(":"); let h = parseInt(p[0]); const m = p[1]; const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; return `${h}:${m} ${ap}`; }).join(" – ")
        : "";

      const iconSize = 3.2;
      const iconGap = 1.8;
      const blockGap = 4;
      const iconTop = y - iconSize + 0.2;

      const drawCalendarIcon = (ix: number, iy: number) => {
        doc.setDrawColor(107, 114, 128);
        doc.setLineWidth(0.35);
        doc.roundedRect(ix, iy, iconSize, iconSize, 0.4, 0.4);
        doc.line(ix, iy + 0.95, ix + iconSize, iy + 0.95);
        doc.line(ix + 0.7, iy - 0.25, ix + 0.7, iy + 0.45);
        doc.line(ix + iconSize - 0.7, iy - 0.25, ix + iconSize - 0.7, iy + 0.45);
      };

      const drawClockIcon = (ix: number, iy: number) => {
        const r = iconSize / 2;
        const cx = ix + r;
        const cy = iy + r;
        doc.setDrawColor(107, 114, 128);
        doc.setLineWidth(0.35);
        doc.circle(cx, cy, r);
        doc.line(cx, cy, cx, cy - 0.95);
        doc.line(cx, cy, cx + 0.75, cy + 0.5);
      };

      let cursorX = textAreaX;
      if (dateStr) {
        drawCalendarIcon(cursorX, iconTop);
        cursorX += iconSize + iconGap;
        doc.text(dateStr, cursorX, y);
        cursorX += doc.getTextWidth(dateStr) + blockGap;
      }

      if (timeStr) {
        drawClockIcon(cursorX, iconTop);
        cursorX += iconSize + iconGap;
        doc.text(timeStr, cursorX, y);
      }

      y += 6 * s;
    }

    // "ORDER OF SERVICE" heading is editable from form and stored in service_type.
    y += 3 * s;
    applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
    doc.text(normalizeOrderOfServiceTitle(t.service_type), x + w / 2, y, { align: "center" });
    y += 8 * s;

    // Service items
    t.bulletin_items.forEach((item) => {
      if (y > startY + h - 18) return;
      if (item.type === "header") {
        applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 1.2, color: [30, 58, 138] });
        doc.text(item.label, x + pad, y); y += 8 * s;
      } else if (item.type === "divider") {
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
      } else {
        applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });
        const labelText = item.time ? `${item.time}  ${item.label}` : item.label;
        const rightColumnWidth = Math.max(12, x + w - pad - personColumnX);
        const personDisplay = formatPersonWithTitle(item);
        const personWrapTolerance = 2.2 * s;
        const personLines = personDisplay
          ? (doc.getTextWidth(personDisplay) <= rightColumnWidth + personWrapTolerance
              ? [personDisplay]
              : doc.splitTextToSize(personDisplay, rightColumnWidth))
          : [];
        const extraNameHeight = Math.max(0, personLines.length - 1) * (4.2 * s);
        const rowHeight = 2 * s + extraNameHeight + 6 * s;
        if (y + rowHeight > startY + h - 2) return;

        doc.text(labelText, x + pad, y);
        if (personLines.length > 0) {
          applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [107, 114, 128] });
          doc.text(personLines, personColumnX, y);
        }
        y += 2 * s + extraNameHeight;
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.2); doc.line(x + pad, y, x + w - pad, y);
        y += 6 * s;
      }
    });

  };

  // Render Page 2: Next Week's Order of Service + Notes (+ Announcements for 2-page layout)
  const renderPDFPage2 = (doc: jsPDF, t: BulletinTemplate, x: number, startY: number, w: number, h: number, logoInfo: { data: string; width: number; height: number } | null, activeFontSize: number, activeLayout: PageLayout) => {
    // 1" top/bottom margin (25.4mm), offset from the existing ~9mm panel margin
    let y = startY + 16;
    const pad = 5;
    const contentWidth = w - pad * 2;
    const s = activeFontSize / 10; // scale factor for spacing
    const personColumnX = x + pad + contentWidth * (2 / 3) - 4.6 * s;

    // Next Week's Order of Service - base × 0.85, uppercase, letter-spaced
    applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
    doc.text(normalizeNextWeekHeading(t.next_week_heading), x + w / 2, y, { align: "center" }); y += 10 * s;

    (t.next_week_items || []).forEach((item) => {
      if (y > startY + h - 18) return;
      if (item.type === "header") {
        applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 1.2, color: [30, 58, 138] });
        doc.text(item.label, x + pad, y); y += 8 * s;
      } else if (item.type === "divider") {
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
      } else {
        applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });
        const labelText = item.time ? `${item.time}  ${item.label}` : item.label;
        const rightColumnWidth = Math.max(12, x + w - pad - personColumnX);
        const personDisplay = formatPersonWithTitle(item);
        const personWrapTolerance = 2.2 * s;
        const personLines = personDisplay
          ? (doc.getTextWidth(personDisplay) <= rightColumnWidth + personWrapTolerance
              ? [personDisplay]
              : doc.splitTextToSize(personDisplay, rightColumnWidth))
          : [];
        const extraNameHeight = Math.max(0, personLines.length - 1) * (4.2 * s);
        const rowHeight = 2 * s + extraNameHeight + 6 * s;
        if (y + rowHeight > startY + h - 2) return;

        doc.text(labelText, x + pad, y);
        if (personLines.length > 0) {
          applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [107, 114, 128] });
          doc.text(personLines, personColumnX, y);
        }
        y += 2 * s + extraNameHeight;
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.2); doc.line(x + pad, y, x + w - pad, y);
        y += 6 * s;
      }
    });

    y += 6 * s;

    // For 2-page layout, also show announcements here
    if (activeLayout === "2-page" && t.announcements.length > 0) {
      doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 6 * s;
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
      doc.text(normalizeAnnouncementsHeading(t.announcements_heading).toUpperCase(), x + w / 2, y, { align: "center" }); y += 6 * s;
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });

      t.announcements.forEach((a, index) => {
        if (y > startY + h - 12) return;

        const itemText = (a.text || "").trim();
        if (!itemText) return;

        const itemWidth = Math.max(18, w - pad * 2 - 10);
        const prefix = `${index + 1}. `;
        const bodyLines = doc.splitTextToSize(itemText, itemWidth);
        const prefixWidth = doc.getTextWidth(prefix);

        doc.text(prefix, x + pad, y);
        doc.text(bodyLines, x + pad + prefixWidth + 1, y);
        y += Math.max(bodyLines.length, 1) * (4.2 * s) + 3 * s;
      });
    }

    // Notes - same size as announcement body text, muted
    if (t.notes) {
      doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 6 * s;
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [107, 114, 128] });
      const notesLines = doc.splitTextToSize(t.notes, Math.max(18, w - pad * 2));
      doc.text(notesLines, x + w / 2, y, { align: "center" });
    }
  };

  // Render Page 3: Announcements (4-page layout only)
  const renderPDFPage3 = (doc: jsPDF, t: BulletinTemplate, x: number, startY: number, w: number, h: number, logoInfo: { data: string; width: number; height: number } | null, activeFontSize: number) => {
    // 1" top/bottom margin (25.4mm), offset from the existing ~9mm panel margin
    let y = startY + 16;
    const pad = 5;
    const s = activeFontSize / 10; // scale factor for spacing

    if (t.announcements.length > 0) {
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
      doc.text(normalizeAnnouncementsHeading(t.announcements_heading).toUpperCase(), x + w / 2, y, { align: "center" }); y += 6 * s;
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });

      t.announcements.forEach((a, index) => {
        if (y > startY + h - 12) return;

        const itemText = (a.text || "").trim();
        if (!itemText) return;

        const itemWidth = Math.max(18, w - pad * 2 - 10);
        const prefix = `${index + 1}. `;
        const bodyLines = doc.splitTextToSize(itemText, itemWidth);
        const prefixWidth = doc.getTextWidth(prefix);

        doc.text(prefix, x + pad, y);
        doc.text(bodyLines, x + pad + prefixWidth + 1, y);
        y += Math.max(bodyLines.length, 1) * (4.2 * s) + 3 * s;
      });
    }
  };

  // Render Page 4: Tithes & Offering (4-page layout only)
  const renderPDFPage4 = (
    doc: jsPDF,
    t: BulletinTemplate,
    x: number,
    startY: number,
    w: number,
    h: number,
    logoInfo: { data: string; width: number; height: number } | null,
    qrInfo: { data: string; width: number; height: number } | null,
    checkinUrl: string | null,
    linkedEventName: string | null,
    activeFontSize: number,
  ) => {
    // 1" top/bottom margin (25.4mm), offset from the existing ~9mm panel margin
    let y = startY + 16;
    const pad = 5;
    const s = activeFontSize / 10; // scale factor for spacing
    const shouldShowTitheNames = t.offering_snapshot?.show_tithe_contributor_names ?? showTitheContributorNames;
    const offeringNotesText = (t.offering_snapshot?.offering_notes || offeringNotes || "").trim();
    const shouldShowYearlyComparison = t.offering_snapshot?.show_yearly_budget_comparison ?? showYearlyBudgetComparison;

    if (offeringData && offeringData.entries.length > 0) {
      // Header - base × 0.85, uppercase, letter-spaced
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
      doc.text(normalizeOfferingHeading(t.offering_snapshot?.offering_heading).toUpperCase(), x + w / 2, y, { align: "center" }); y += 5 * s;

      // Date shown directly under header
      applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
      doc.text(format(new Date(offeringDate + "T00:00"), "MMMM d, yyyy"), x + w / 2, y, { align: "center" });
      y += 6 * s;

      // --- Tithe Contributors ---
      if (offeringData.titheContributions.length > 0 || offeringData.tithesTotal > 0) {
        if (shouldShowTitheNames && offeringData.titheContributions.length > 0) {
          applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [107, 114, 128] });
          offeringData.titheContributions.forEach((tc) => {
            if (y > startY + h - 10) return;
            doc.text(tc.name, x + pad, y);
            doc.text(`$${tc.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
            y += 6 * s;
          });

          doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
        }

        applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize, color: [31, 41, 55] });
        doc.text("Tithes Total", x + pad, y);
        doc.text(`$${offeringData.tithesTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
        y += 8 * s;
      }

      // --- Offerings ---
      if (offeringData.offeringEntries.length > 0) {
        applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });
        offeringData.offeringEntries.forEach((e) => {
          applyBulletinPdfTextStyle(doc, { family: "sans", weight: e.category.trim().toLowerCase() === "offerings" ? "bold" : "normal", fontSize: activeFontSize, color: [31, 41, 55] });
          doc.text(e.category, x + pad, y);
          applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize, color: [31, 41, 55] });
          doc.text(`$${e.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
          y += 7 * s;
        });
        y += 2 * s;
      }

      // --- Total Contributions ---
      doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
      applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize, color: [30, 58, 138] });
      doc.text("Total Contributions", x + pad, y);
      doc.text(`$${offeringData.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
      y += 10 * s;

      if (shouldShowYearlyComparison && yearlyBudgetComparison) {
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
        applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize * 0.9, color: [31, 41, 55] });
        doc.text("Yearly Budget", x + pad, y);
        doc.text(`$${yearlyBudgetComparison.budgetTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
        y += 6 * s;

        doc.text("Yearly Actual", x + pad, y);
        doc.text(`$${yearlyBudgetComparison.actualTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, x + w - pad, y, { align: "right" });
        y += 8 * s;
      }

      if (offeringNotesText) {
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5 * s;
        applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize, color: [31, 41, 55] });
        const noteLines = doc.splitTextToSize(offeringNotesText, w - 10);
        doc.text(noteLines, x + pad, y);
        y += noteLines.length * (4 * s) + 3 * s;
      }

      if (qrInfo && checkinUrl) {
        const qrSize = 28;
        const minBottomSpace = qrSize + 18;
        if (y > startY + h - minBottomSpace) {
          y = startY + h - minBottomSpace;
        }

        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.line(x + pad, y, x + w - pad, y); y += 5;
        applyBulletinPdfTextStyle(doc, { family: "sans", weight: "bold", fontSize: activeFontSize * 0.85, color: [107, 114, 128] });
        doc.text("Attendance Check-in", x + w / 2, y, { align: "center" }); y += 3;
        if (linkedEventName) {
          applyBulletinPdfTextStyle(doc, { family: "sans", fontSize: activeFontSize * 0.8, color: [107, 114, 128] });
          doc.text(linkedEventName, x + w / 2, y, { align: "center" });
          y += 2;
        }

        const qrX = x + (w - qrSize) / 2;
        doc.addImage(qrInfo.data, "PNG", qrX, y, qrSize, qrSize);
      }
    }
  };

  // ============ PREVIEW CONTENT COMPONENT ============
  const PreviewPanel = ({ template, page }: { template: BulletinTemplate; page?: 1 | 2 | 3 | 4 }) => {
    const baseFontSize = fontSize;
    const churchName = profile?.church_name || "Church";
    const previewChurchNameSize = fitChurchNameSize(churchName, baseFontSize);
    const linkedEvent = bulletinEvents.find((event) => event.id === template.event_id);
    const shouldShowTitheNames = template.offering_snapshot?.show_tithe_contributor_names ?? showTitheContributorNames;
    const offeringNotesText = (template.offering_snapshot?.offering_notes || offeringNotes || "").trim();
    const shouldShowYearlyComparison = template.offering_snapshot?.show_yearly_budget_comparison ?? showYearlyBudgetComparison;
    const checkinUrl = template.event_id ? `${window.location.origin}/checkin?event_id=${template.event_id}` : null;
    
    // Render different content based on page number and layout
    const renderPage1 = () => (
      <div className="space-y-3" style={{ fontSize: `${baseFontSize}px`, padding: "4% 5%" }}>
        {/* Church Header */}
        <div className="flex items-start gap-4">
          {profile?.logo_url && (
            <img
              src={profile.logo_url}
              alt="Logo"
              className="w-16 h-16 object-contain shrink-0"
            />
          )}
          <div className="min-w-0 flex-1 text-left">
            <h2 className="font-bold uppercase tracking-[0.04em]" style={{ fontFamily: "ui-serif, Georgia, serif", fontSize: `${previewChurchNameSize}px`, color: "#0f3767", whiteSpace: "nowrap", lineHeight: 1.1 }}>
              {churchName}
            </h2>
            {profile?.bible_verse && (
              <p style={{ fontFamily: "ui-serif, Georgia, serif", fontStyle: "italic", fontSize: `${baseFontSize * 0.95}px`, color: "#1f2937", marginTop: "4px" }}>
                "{profile.bible_verse}"{profile.bible_reference ? ` (${profile.bible_reference})` : ""}
              </p>
            )}
            {(profile?.address || profile?.city) && (
              <p style={{ fontFamily: "ui-serif, Georgia, serif", fontWeight: 700, fontSize: `${baseFontSize * 1.05}px`, color: "#111827", marginTop: "4px" }}>
                {[profile?.address, profile?.city, profile?.state, profile?.zip_code].filter(Boolean).join(", ")}
              </p>
            )}
            {(template.service_date || template.service_time) && (
              <p className="flex flex-wrap items-center gap-2" style={{ fontSize: `${baseFontSize}px`, color: "#6b7280", marginTop: "8px" }}>
                {template.service_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(template.service_date + "T00:00"), "MMMM d, yyyy")}
                  </span>
                )}
                {template.service_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {template.service_time.split(" - ").map(t => formatTime12(t.trim())).join(" – ")}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Order of Service */}
        <div className="space-y-1">
          <h4 
            className="font-semibold uppercase text-center" 
            style={{ 
              fontSize: `${baseFontSize * 0.85}px`, 
              color: "#6b7280",
              letterSpacing: "0.09em",
              marginBottom: "10px"
            }}
          >
            {normalizeOrderOfServiceTitle(template.service_type)}
          </h4>
          {template.bulletin_items.map((item) => {
            if (item.type === "divider") return (
              <div key={item.id} className="border-t" style={{ borderColor: "#e5e7eb", margin: "4px 0" }} />
            );
            if (item.type === "header") return (
              <p 
                key={item.id} 
                className="font-bold" 
                style={{ fontSize: `${baseFontSize * 1.2}px`, color: "#1e3a8a", paddingTop: "6px" }}
              >
                {item.label}
              </p>
            );
            return (
              <div key={item.id} className="grid grid-cols-[2fr_1fr] items-start border-b" style={{ borderColor: "#e5e7eb", paddingTop: "5px", paddingBottom: "5px" }}>
                <div className="flex items-center gap-2 min-w-0">
                  {item.time && <span className="font-mono shrink-0" style={{ fontSize: `${baseFontSize}px`, color: "#1f2937", width: "3rem" }}>{item.time}</span>}
                  <span className="truncate" style={{ fontSize: `${baseFontSize}px`, color: "#1f2937" }}>{item.label}</span>
                </div>
                <div className="min-w-0 pl-0 text-left">
                  {formatPersonWithTitle(item) && <span className="italic block whitespace-normal break-words leading-snug" style={{ fontSize: `${baseFontSize}px`, color: "#6b7280" }}>{formatPersonWithTitle(item)}</span>}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    );

    const renderPage2 = () => (
      <div className="space-y-3" style={{ fontSize: `${baseFontSize}px`, padding: "10% 5%" }}>
        {/* Next Week's Order of Service */}
        <div className="space-y-1">
          <h4 
            className="font-semibold uppercase text-center" 
            style={{ 
              fontSize: `${baseFontSize * 0.85}px`, 
              color: "#6b7280",
              letterSpacing: "0.09em",
              marginBottom: "10px"
            }}
          >
            {normalizeNextWeekHeading(template.next_week_heading)}
          </h4>
          {(template.next_week_items || []).map((item) => {
            if (item.type === "divider") return (
              <div key={item.id} className="border-t" style={{ borderColor: "#e5e7eb", margin: "4px 0" }} />
            );
            if (item.type === "header") return (
              <p 
                key={item.id} 
                className="font-bold" 
                style={{ fontSize: `${baseFontSize * 1.2}px`, color: "#1e3a8a", paddingTop: "6px" }}
              >
                {item.label}
              </p>
            );
            return (
              <div key={item.id} className="grid grid-cols-[2fr_1fr] items-start border-b" style={{ borderColor: "#e5e7eb", paddingTop: "5px", paddingBottom: "5px" }}>
                <div className="flex items-center gap-2 min-w-0">
                  {item.time && <span className="font-mono shrink-0" style={{ fontSize: `${baseFontSize}px`, color: "#1f2937", width: "3rem" }}>{item.time}</span>}
                  <span className="truncate" style={{ fontSize: `${baseFontSize}px`, color: "#1f2937" }}>{item.label}</span>
                </div>
                <div className="min-w-0 pl-0 text-left">
                  {formatPersonWithTitle(item) && <span className="italic block whitespace-normal break-words leading-snug" style={{ fontSize: `${baseFontSize}px`, color: "#6b7280" }}>{formatPersonWithTitle(item)}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* For 2-page layout: Also show announcements here */}
        {pageLayout === "2-page" && template.announcements.length > 0 && (
          <>
            <div className="border-t" style={{ borderColor: "#e5e7eb", marginTop: "8px", marginBottom: "8px" }} />
            <div>
              <h4 
                className="font-semibold uppercase text-center" 
                style={{ 
                  fontSize: `${baseFontSize * 0.85}px`, 
                  color: "#6b7280",
                  letterSpacing: "0.09em",
                  marginBottom: "10px"
                }}
              >
                {normalizeAnnouncementsHeading(template.announcements_heading)}
              </h4>
              <div className="space-y-4">
                {template.announcements.map((a) => (
                  <p key={a.id} style={{ fontSize: `${baseFontSize}px`, color: "#1f2937" }}>{a.text}</p>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Notes */}
        {template.notes && (
          <>
            <div className="border-t" style={{ borderColor: "#e5e7eb", marginTop: "8px", marginBottom: "8px" }} />
            <p className="italic text-center" style={{ fontSize: `${baseFontSize}px`, color: "#6b7280" }}>{template.notes}</p>
          </>
        )}
      </div>
    );

    const renderPage3 = () => (
      <div className="space-y-3" style={{ fontSize: `${baseFontSize}px`, padding: "10% 5%" }}>
        {/* Announcements */}
        {template.announcements.length > 0 && (
          <div>
            <h4 
              className="font-semibold uppercase text-center" 
              style={{ 
                fontSize: `${baseFontSize * 0.85}px`, 
                color: "#6b7280",
                letterSpacing: "0.09em",
                marginBottom: "10px"
              }}
            >
              {normalizeAnnouncementsHeading(template.announcements_heading)}
            </h4>
            <div className="space-y-4">
              {template.announcements.map((a) => (
                <p key={a.id} style={{ fontSize: `${baseFontSize}px`, color: "#1f2937" }}>{a.text}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    const renderPage4 = () => (
      <div className="space-y-3" style={{ fontSize: `${baseFontSize}px`, padding: "10% 5%" }}>
        {/* Tithes & Offering */}
        {offeringData && offeringData.entries.length > 0 && (
          <div>
            <h4 
              className="font-semibold uppercase text-center" 
              style={{ 
                fontSize: `${baseFontSize * 0.85}px`, 
                color: "#6b7280",
                letterSpacing: "0.09em",
                marginBottom: "4px"
              }}
            >
              {normalizeOfferingHeading(template.offering_snapshot?.offering_heading)}
            </h4>

            <p className="text-center" style={{ fontSize: `${baseFontSize * 0.85}px`, color: "#6b7280", marginTop: "0px", marginBottom: "6px" }}>
              {format(new Date(offeringDate + "T00:00"), "MMMM d, yyyy")}
            </p>

            {/* Tithe contributors list: name + amount */}
            {(offeringData.titheContributions.length > 0 || offeringData.tithesTotal > 0) && (
              <div style={{ marginTop: "8px" }}>
                {shouldShowTitheNames && offeringData.titheContributions.length > 0 && (
                  <>
                    <div className="space-y-1">
                      {offeringData.titheContributions.map((tc, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: `${baseFontSize}px` }}>
                          <span style={{ color: "#6b7280" }}>{tc.name}</span>
                          <span style={{ color: "#6b7280" }}>${tc.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between font-bold" style={{ borderColor: "#e5e7eb", fontSize: `${baseFontSize}px`, paddingTop: shouldShowTitheNames && offeringData.titheContributions.length > 0 ? "4px" : "0px", marginTop: shouldShowTitheNames && offeringData.titheContributions.length > 0 ? "6px" : "0px", borderTopWidth: shouldShowTitheNames && offeringData.titheContributions.length > 0 ? "1px" : "0px", borderTopStyle: "solid" }}>
                  <span style={{ color: "#1f2937" }}>Tithes Total</span>
                  <span style={{ color: "#1f2937" }}>${offeringData.tithesTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {/* Offerings */}
            {offeringData.offeringEntries.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <div className="space-y-1">
                  {offeringData.offeringEntries.map((e) => (
                    <div key={e.category} className="flex items-center justify-between" style={{ fontSize: `${baseFontSize}px`, paddingTop: "5px", paddingBottom: "5px" }}>
                      <span className={e.category.trim().toLowerCase() === "offerings" ? "font-bold" : ""} style={{ color: "#1f2937" }}>{e.category}</span>
                      <span className="font-bold" style={{ color: "#1f2937" }}>${e.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Contributions */}
            <div className="flex items-center justify-between border-t font-bold" style={{ borderColor: "#e5e7eb", fontSize: `${baseFontSize}px`, paddingTop: "4px", marginTop: "8px" }}>
              <span style={{ color: "#1e3a8a" }}>Total Contributions</span>
              <span style={{ color: "#1e3a8a" }}>${offeringData.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            {shouldShowYearlyComparison && yearlyBudgetComparison && (
              <div className="border-t" style={{ borderColor: "#e5e7eb", marginTop: "8px", paddingTop: "8px" }}>
                <div className="flex items-center justify-between" style={{ fontSize: `${baseFontSize * 0.92}px`, color: "#1f2937" }}>
                  <span>Yearly Budget</span>
                  <span>${yearlyBudgetComparison.budgetTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center justify-between mt-1" style={{ fontSize: `${baseFontSize * 0.92}px`, color: "#1f2937" }}>
                  <span>Yearly Actual</span>
                  <span>${yearlyBudgetComparison.actualTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {offeringNotesText && (
              <div className="border-t" style={{ borderColor: "#e5e7eb", marginTop: "8px", paddingTop: "8px" }}>
                <p style={{ fontSize: `${baseFontSize}px`, color: "#1f2937" }}>{offeringNotesText}</p>
              </div>
            )}

            {checkinUrl && (
              <div className="border-t" style={{ borderColor: "#e5e7eb", marginTop: "10px", paddingTop: "10px" }}>
                <p className="font-semibold text-center uppercase" style={{ fontSize: `${baseFontSize * 0.78}px`, color: "#6b7280", letterSpacing: "0.08em" }}>
                  Attendance Check-in
                </p>
                {linkedEvent && (
                  <p className="text-center" style={{ fontSize: `${baseFontSize * 0.82}px`, color: "#6b7280", marginTop: "4px" }}>
                    {linkedEvent.name}
                  </p>
                )}
                <div className="flex justify-center mt-2">
                  <QRCodeSVG value={checkinUrl} size={96} level="H" includeMargin />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );

    // Return the appropriate page content
    if (page === 1) return renderPage1();
    if (page === 2) return renderPage2();
    if (page === 3) return renderPage3();
    if (page === 4) return renderPage4();
    
    // Default: show all pages (for old backward compat)
    return (
      <div className="space-y-4" style={{ fontSize: `${baseFontSize}px` }}>
        {renderPage1()}
        <Separator />
        {renderPage2()}
      </div>
    );
  };

  const uniqueServiceTypes = useMemo(() => [...new Set(bulletins.map((t) => t.service_type))], [bulletins]);

  const filteredBulletins = useMemo(() => bulletins.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.name.toLowerCase().includes(q) || t.service_type.toLowerCase().includes(q);
    const matchType = typeFilters.size === 0 || typeFilters.has(t.service_type);
    const serviceDay = t.service_date ? startOfDay(parseISO(t.service_date)) : null;
    const matchesDateStart = !dateRangeStart || !serviceDay || !isBefore(serviceDay, startOfDay(dateRangeStart));
    const matchesDateEnd = !dateRangeEnd || !serviceDay || !isAfter(serviceDay, startOfDay(dateRangeEnd));
    return matchSearch && matchType && matchesDateStart && matchesDateEnd;
  }), [bulletins, search, typeFilters, dateRangeStart, dateRangeEnd]);

  const today = format(new Date(), "yyyy-MM-dd");
  const upcomingBulletins = useMemo(() => filteredBulletins.filter((t) => !t.service_date || t.service_date >= today), [filteredBulletins, today]);
  const pastBulletins = useMemo(() => filteredBulletins.filter((t) => !!t.service_date && t.service_date < today), [filteredBulletins, today]);
  const participantSourceBulletins = useMemo(
    () => bulletins.filter((t) => !!t.service_date && t.service_date < today),
    [bulletins, today],
  );

  const participantCredits = useMemo(() => {
    const credits: ParticipantCredit[] = [];

    const splitPeople = (value: string) =>
      value
        .split(/,|&|\band\b|\//gi)
        .map((part) => part.trim())
        .filter(Boolean);

    participantSourceBulletins.forEach((bulletin) => {
      (bulletin.bulletin_items || []).forEach((item) => {
        if (item.type !== "item") return;
        if (!item.person?.trim()) return;
        if (!item.label?.trim()) return;

        splitPeople(item.person).forEach((personName) => {
          credits.push({
            name: personName,
            role: item.label.trim(),
            serviceDate: bulletin.service_date || "",
            serviceType: bulletin.service_type || FALLBACK_MINISTRY_LABEL,
            bulletinId: bulletin.id,
            bulletinName: bulletin.name,
          });
        });
      });
    });

    return credits;
  }, [participantSourceBulletins]);

  const participantRoleOptions = useMemo(() => {
    const roles = Array.from(new Set(participantCredits.map((credit) => credit.role))).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    return roles;
  }, [participantCredits]);

  const participantCards = useMemo(() => {
    const normalizedSearch = participantSearch.trim().toLowerCase();
    const grouped = new Map<string, { name: string; roles: Set<string>; credits: ParticipantCredit[] }>();

    participantCredits.forEach((credit) => {
      if (participantRoleFilter !== "all" && credit.role !== participantRoleFilter) return;

      const serviceDay = credit.serviceDate ? startOfDay(new Date(`${credit.serviceDate}T00:00:00`)) : null;
      const matchesDateStart = !participantDateStart || !serviceDay || !isBefore(serviceDay, startOfDay(participantDateStart));
      const matchesDateEnd = !participantDateEnd || !serviceDay || !isAfter(serviceDay, startOfDay(participantDateEnd));
      if (!matchesDateStart || !matchesDateEnd) return;

      const key = credit.name.toLowerCase();
      const current = grouped.get(key) || { name: credit.name, roles: new Set<string>(), credits: [] };
      current.roles.add(credit.role);
      current.credits.push(credit);
      grouped.set(key, current);
    });

    const entries = Array.from(grouped.values())
      .map((entry) => ({
        name: entry.name,
        roles: Array.from(entry.roles).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
        bulletinCount: new Set(entry.credits.map((credit) => credit.bulletinId)).size,
        totalServices: entry.credits.length,
        history: [...entry.credits].sort((a, b) => b.serviceDate.localeCompare(a.serviceDate)),
      }))
      .filter((entry) => {
        if (!normalizedSearch) return true;
        return entry.name.toLowerCase().includes(normalizedSearch);
      })
      .sort((a, b) => b.totalServices - a.totalServices || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return entries;
  }, [participantCredits, participantRoleFilter, participantSearch, participantDateStart, participantDateEnd]);

  const getInitials = (fullName: string) =>
    fullName
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase())
      .join("")
      .slice(0, 2) || "--";

  const avatarToneForName = (fullName: string) => {
    return "bg-primary/10 text-primary";
  };

  return (
    <div className="space-y-4">
      <Tabs value={bulletinTab} onValueChange={(v) => {
        setBulletinTab(v);
        setSearch("");
        setTemplateSearch("");
        setParticipantSearch("");
        setParticipantRoleFilter("all");
        setParticipantDateStart(undefined);
        setParticipantDateEnd(undefined);
      }} className="space-y-4">
        <TabsList className="h-9 rounded-none bg-muted p-1 gap-1">
          <TabsTrigger value="upcoming" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarCheck className="w-3.5 h-3.5 mr-1.5" />Upcoming
          </TabsTrigger>
          <TabsTrigger value="past" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <History className="w-3.5 h-3.5 mr-1.5" />Past
          </TabsTrigger>
          <TabsTrigger value="templates" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" />Templates
          </TabsTrigger>
          <TabsTrigger value="participants" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="w-3.5 h-3.5 mr-1.5" />Participants
          </TabsTrigger>
        </TabsList>

        {(["upcoming", "past"] as const).includes(bulletinTab as "upcoming" | "past") && <TableFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search bulletins..."
          searchFirstOnMobile
          mobileControlsFirst
          compactDesktopFilters
          dateRange={{
            start: dateRangeStart,
            end: dateRangeEnd,
            onStartChange: (d) => setDateRangeStart(d ?? undefined),
            onEndChange: (d) => setDateRangeEnd(d ?? undefined),
            onClear: () => {
              setDateRangeStart(undefined);
              setDateRangeEnd(undefined);
            },
          }}
          filterGroups={[{
            key: "service_type",
            label: "Ministry",
            options: uniqueServiceTypes,
            selected: typeFilters,
            onToggle: (v) => setTypeFilters((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; }),
            onClear: () => setTypeFilters(new Set()),
          }]}
          leadingControls={
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button onClick={() => setViewMode("cards")} className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="Card view">
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode("list")} className={cn("p-2 transition-colors", viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="List view">
                <List className="w-4 h-4" />
              </button>
            </div>
          }
          extraControls={
            <div className="flex items-center gap-2">
              <Button size="sm" className="h-9 px-3 shrink-0" onClick={() => openEditor()}>
                {isPreview && <span className="mr-2"></span>}
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Bulletin</span>
              </Button>
            </div>
          }
        />}

        {/* helper to render a bulletin list/grid */}
        {(["upcoming", "past"] as const).map((tab) => {
          const list = tab === "upcoming" ? upcomingBulletins : pastBulletins;
          return (
            <TabsContent key={tab} value={tab} className="space-y-4">
              {isLoading ? (
                <div className="stat-card text-center py-12 text-muted-foreground">Loading bulletins...</div>
              ) : bulletins.length === 0 ? (
                <div className="stat-card text-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No bulletins yet. Create your first service bulletin!</p>
                  <Button size="sm" variant="outline" className="mt-4" onClick={() => openEditor()}>
                    {isPreview && <span className="mr-2"></span>}
                    <Plus className="w-4 h-4 mr-2" /> Create Bulletin
                  </Button>
                </div>
              ) : list.length === 0 ? (
                <div className="stat-card text-center py-12 text-muted-foreground text-sm">
                  {search || typeFilters.size > 0 || !!dateRangeStart || !!dateRangeEnd ? "No bulletins match your filters." : tab === "upcoming" ? "No upcoming bulletins." : "No past bulletins."}
                </div>
              ) : viewMode === "list" ? (
                <div className="stat-card p-0 overflow-hidden divide-y divide-border">
                  {list.map((t) => (
                    <div
                      key={t.id}
                      className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px_140px_auto_auto] items-center gap-2 sm:gap-3 px-5 py-3 hover:bg-secondary/20 transition-colors cursor-pointer"
                      onClick={() => openPreview(t)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <BookOpen className="w-4 h-4 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">{t.name}</p>
                          <p className="text-xs text-muted-foreground sm:hidden">
                            {t.service_type}
                            {t.service_date ? " · " + format(new Date(t.service_date + "T00:00"), "MMM d, yyyy") : ""}
                          </p>
                        </div>
                      </div>

                      <p className="hidden sm:block text-sm text-muted-foreground truncate">{t.service_type}</p>

                      <p className="hidden sm:block text-sm text-muted-foreground">
                        {t.service_date ? format(new Date(t.service_date + "T00:00"), "MMM d, yyyy") : "—"}
                      </p>

                      <span className="text-xs text-muted-foreground hidden sm:block">{(t.bulletin_items || []).filter(i => i.type === "item").length} items</span>

                      <div className="flex items-center justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-secondary transition-colors" onClick={(e) => e.stopPropagation()}><MoreHorizontal className="w-4 h-4 text-muted-foreground" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditor(t); }}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateTemplate(t); }}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPDF(t); }}><Download className="w-4 h-4 mr-2" /> PDF</DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteId(t.id); }} className="text-destructive focus:text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((t) => (
                    <Card key={t.id} className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer" onClick={() => openPreview(t)}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-sm font-semibold truncate">{t.name}</CardTitle>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <Badge variant="secondary" className="text-xs">{t.service_type}</Badge>
                              {t.service_date && <span className="text-xs text-muted-foreground">{format(new Date(t.service_date + "T00:00"), "MMM d, yyyy")}</span>}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded hover:bg-secondary transition-colors" onClick={(e) => e.stopPropagation()}><MoreHorizontal className="w-4 h-4 text-muted-foreground" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditor(t); }}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateTemplate(t); }}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPDF(t); }}><Download className="w-4 h-4 mr-2" /> PDF</DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteId(t.id); }} className="text-destructive focus:text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-xs text-muted-foreground">{(t.bulletin_items || []).filter(i => i.type === "item").length} items · {(t.announcements || []).length} announcements</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          );
        })}

        {/* ===== TEMPLATES TAB ===== */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FileText className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search templates..."
                className="pl-9"
              />
            </div>
          </div>

          {isLoadingTemplates ? (
            <div className="stat-card text-center py-12 text-muted-foreground">Loading templates...</div>
          ) : savedTemplates.length === 0 ? (
            <div className="stat-card text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No templates yet. Open a bulletin and click Save as Template.</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="stat-card text-center py-12 text-muted-foreground text-sm">No templates match your search.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((t) => {
                const displayName = t.template_name || t.name;
                const itemCount = (t.bulletin_items || []).filter((i) => i.type === "item").length;
                return (
                  <Card key={t.id} className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <CardTitle className="text-sm font-semibold truncate">{displayName}</CardTitle>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{t.service_type || FALLBACK_MINISTRY_LABEL}</Badge>
                            <span className="text-xs text-muted-foreground">{format(new Date(t.created_at), "MMM d, yyyy")}</span>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-secondary transition-colors"><MoreHorizontal className="w-4 h-4 text-muted-foreground" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDeleteTemplateId(t.id)} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      <p className="text-xs text-muted-foreground">{itemCount} service items · {(t.announcements || []).length} announcements</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPreview(t)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            openTemplateAsNewDraft(t);
                          }}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Use Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== PARTICIPANTS TAB ===== */}
        <TabsContent value="participants" className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center border border-border rounded-lg overflow-hidden w-fit">
              <button
                type="button"
                onClick={() => setParticipantViewMode("table")}
                className={cn("p-2 transition-colors", participantViewMode === "table" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setParticipantViewMode("list")}
                className={cn("p-2 transition-colors", participantViewMode === "list" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                title="List view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>

            <div className="relative flex-1">
              <Users className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                placeholder="Search participants..."
                className="pl-9"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 px-3 w-full sm:w-auto">
                  <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] space-y-4" align="end">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
                  <Select value={participantRoleFilter} onValueChange={setParticipantRoleFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All roles</SelectItem>
                      {participantRoleOptions.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">From date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {participantDateStart ? format(participantDateStart, "MMM d, yyyy") : "Any"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <UICalendar
                        mode="single"
                        selected={participantDateStart}
                        onSelect={(date) => setParticipantDateStart(date ?? undefined)}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">To date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {participantDateEnd ? format(participantDateEnd, "MMM d, yyyy") : "Any"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <UICalendar
                        mode="single"
                        selected={participantDateEnd}
                        onSelect={(date) => setParticipantDateEnd(date ?? undefined)}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setParticipantRoleFilter("all");
                      setParticipantDateStart(undefined);
                      setParticipantDateEnd(undefined);
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {participantCards.length === 0 ? (
            <div className="stat-card text-center py-12 text-muted-foreground text-sm">
              No past participant credits found for the current filters.
            </div>
          ) : (
            participantViewMode === "table" ? (
              <div className="stat-card p-0 overflow-hidden">
                <div className="hidden sm:grid sm:grid-cols-[minmax(0,0.85fr)_140px_140px_28px] gap-4 px-5 py-2 border-b border-border bg-secondary/30 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Name</span>
                  <span>Bulletins</span>
                  <span>Services</span>
                  <span className="w-6" />
                </div>
                <div className="divide-y divide-border">
                  {participantCards.map((card) => (
                    <div
                      key={card.name}
                      className="w-full text-left grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,0.85fr)_140px_140px_28px] items-center gap-4 px-5 py-3 transition-colors hover:bg-secondary/30"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/communication/participants/${encodeURIComponent(card.name)}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(`/communication/participants/${encodeURIComponent(card.name)}`);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold", avatarToneForName(card.name))}>
                          <span className="leading-none">{getInitials(card.name)}</span>
                        </div>
                        <p className="text-foreground font-semibold text-sm truncate">{card.name}</p>
                      </div>
                      <Badge variant="secondary" className="hidden sm:inline-flex text-xs justify-self-start">{card.bulletinCount} {card.bulletinCount === 1 ? "bulletin" : "bulletins"}</Badge>
                      <Badge variant="secondary" className="text-xs justify-self-start">{card.totalServices} {card.totalServices === 1 ? "service" : "services"}</Badge>

                      <ChevronRight className="w-4 h-4 text-muted-foreground justify-self-end" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {participantCards.map((card) => (
                  <button
                    key={card.name}
                    type="button"
                    className="stat-card text-left hover:bg-secondary/20 transition-colors"
                    onClick={() => navigate(`/communication/participants/${encodeURIComponent(card.name)}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold", avatarToneForName(card.name))}>
                        <span className="leading-none">{getInitials(card.name)}</span>
                      </div>
                      <p className="text-foreground font-semibold text-sm truncate">{card.name}</p>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">{card.bulletinCount} {card.bulletinCount === 1 ? "bulletin" : "bulletins"}</Badge>
                      <Badge variant="secondary" className="text-xs">{card.totalServices} {card.totalServices === 1 ? "service" : "services"}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
        </TabsContent>
      </Tabs>

      {/* Delete template confirmation */}
      <AlertDialog open={!!deleteTemplateId} onOpenChange={(open) => !open && setDeleteTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>This removes the template permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTemplateId && deleteTemplateMutation.mutate(deleteTemplateId)}
              disabled={deleteTemplateMutation.isPending}
            >
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ===== EDITOR SHEET ===== */}
      <Sheet open={editorOpen} onOpenChange={(v) => !v && setEditorOpen(false)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto [&>button]:hidden">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {editingTemplate ? "Edit Bulletin" : "New Bulletin"}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Bulletin Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunday Worship - March 10" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-10 w-full justify-start text-left font-normal">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {serviceDate ? format(parseISO(serviceDate), "EEEE, MMM d, yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <UICalendar
                      mode="single"
                      selected={serviceDate ? parseISO(serviceDate) : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        setServiceDate(format(d, "yyyy-MM-dd"));
                      }}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Start Time</Label>
                <TimePicker value={serviceTime} onChange={setServiceTime} />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <TimePicker value={serviceEndTime} onChange={setServiceEndTime} />
              </div>
              <div className="space-y-2">
                <Label>Page Layout</Label>
                <Select value={pageLayout} onValueChange={(v) => setPageLayout(v as PageLayout)}>
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2-page">2-Page</SelectItem>
                    <SelectItem value="4-page">4-Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Font Size</Label>
                <Select value={fontSize.toString()} onValueChange={(v) => setFontSize(parseInt(v))}>
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 11, 12].map((size) => (
                      <SelectItem key={size} value={size.toString()}>{size}px</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Order of Service */}
            <div>
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">Order of Service</h3>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addItem("header")}>
                      + Header
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addItem("divider")}>
                      + Divider
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addItem("item")}>
                      + Item
                    </Button>
                  </div>
                </div>
                <Input
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  placeholder="e.g. ORDER OF SERVICE"
                  list="bulletin-title-options"
                />
                <datalist id="bulletin-title-options">
                  {ministryOptions.map((ministry) => (
                    <option key={ministry} value={ministry} />
                  ))}
                </datalist>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
                <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {items.map((item) => (
                      <SortableBulletinRow key={item.id} id={item.id}>
                        {item.type === "divider" ? (
                          <div className="flex-1 border-t border-border my-2" />
                        ) : item.type === "header" ? (
                          <Input
                            value={item.label}
                            onChange={(e) => updateItem(item.id, "label", e.target.value)}
                            className="flex-1 font-semibold text-sm h-9 bg-secondary/30"
                            placeholder="Section header..."
                          />
                        ) : (
                          <>
                            <Input
                              value={item.label}
                              onChange={(e) => updateItem(item.id, "label", e.target.value)}
                              className="flex-1 text-sm h-9 min-w-0"
                              placeholder="Service item..."
                            />
                            <div className="flex-1 min-w-0 flex items-center gap-2">
                              <Input
                                value={item.personTitle || ""}
                                onChange={(e) => updateItem(item.id, "personTitle", e.target.value)}
                                placeholder="Title"
                                className="w-[78px] text-sm h-9 shrink-0"
                              />
                              <MemberAutocomplete
                                value={item.person}
                                onChange={(v) => updateItem(item.id, "person", v)}
                                placeholder="Person(s), comma-separated"
                                appendOnSelect
                                chips
                                className="text-sm flex-1 min-w-[170px]"
                              />
                            </div>
                          </>
                        )}

                        <button type="button" onClick={() => removeItem(item.id)} className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </SortableBulletinRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

            </div>

            <Separator />

            {/* Next Week's Order of Service */}
            <div>
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Next Week's Heading</Label>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addNextWeekItem("header")}>
                      + Header
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addNextWeekItem("divider")}>
                      + Divider
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => addNextWeekItem("item")}>
                      + Item
                    </Button>
                  </div>
                </div>
                <Input
                  value={nextWeekHeading}
                  onChange={(e) => setNextWeekHeading(e.target.value)}
                  placeholder="e.g. NEXT WEEK'S ORDER OF SERVICE"
                />
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleNextWeekDragEnd}>
                <SortableContext items={nextWeekItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {nextWeekItems.map((item) => (
                      <SortableBulletinRow key={item.id} id={item.id}>
                        {item.type === "divider" ? (
                          <div className="flex-1 border-t border-border my-2" />
                        ) : item.type === "header" ? (
                          <Input
                            value={item.label}
                            onChange={(e) => updateNextWeekItem(item.id, "label", e.target.value)}
                            className="flex-1 font-semibold text-sm h-9 bg-secondary/30"
                            placeholder="Section header..."
                          />
                        ) : (
                          <>
                            <Input
                              value={item.label}
                              onChange={(e) => updateNextWeekItem(item.id, "label", e.target.value)}
                              className="flex-1 text-sm h-9 min-w-0"
                              placeholder="Service item..."
                            />
                            <div className="flex-1 min-w-0 flex items-center gap-2">
                              <Input
                                value={item.personTitle || ""}
                                onChange={(e) => updateNextWeekItem(item.id, "personTitle", e.target.value)}
                                placeholder="Title"
                                className="w-[78px] text-sm h-9 shrink-0"
                              />
                              <MemberAutocomplete
                                value={item.person}
                                onChange={(v) => updateNextWeekItem(item.id, "person", v)}
                                placeholder="Person(s), comma-separated"
                                appendOnSelect
                                chips
                                className="text-sm flex-1 min-w-[170px]"
                              />
                            </div>
                          </>
                        )}

                        <button type="button" onClick={() => removeNextWeekItem(item.id)} className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </SortableBulletinRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

            </div>

            <Separator />

            {/* Announcements */}
            <div>
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">Announcements</h3>
                  <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={addAnnouncement}>
                    + Add
                  </Button>
                </div>
                <Input
                  value={announcementsHeading}
                  onChange={(e) => setAnnouncementsHeading(e.target.value)}
                  placeholder="e.g. ANNOUNCEMENTS"
                />
              </div>
              {announcements.length === 0 ? (
                <p className="text-xs text-muted-foreground">No announcements added.</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAnnouncementDragEnd}>
                  <SortableContext items={announcements.map(a => a.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {announcements.map((a) => (
                        <SortableBulletinRow key={a.id} id={a.id}>
                          <Textarea
                            value={a.text}
                            onChange={(e) => updateAnnouncement(a.id, e.target.value)}
                            className="text-sm min-h-[60px]"
                            placeholder="Announcement text..."
                            rows={2}
                          />
                          <button type="button" onClick={() => removeAnnouncement(a.id)} className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </SortableBulletinRow>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            <Separator />

            {/* Tithes & Offering Info */}
            <div>
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="text-sm font-semibold text-foreground">Tithes & Offering Summary</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 w-40 justify-start text-left font-normal">
                        <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                        {offeringDate ? format(parseISO(offeringDate), "MMM d, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <UICalendar
                        mode="single"
                        selected={offeringDate ? parseISO(offeringDate) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          setOfferingDate(format(d, "yyyy-MM-dd"));
                        }}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1">
                    <Label htmlFor="show-tithe-contributors" className="text-xs text-muted-foreground">Show tithe contributors' names</Label>
                    <Switch
                      id="show-tithe-contributors"
                      checked={showTitheContributorNames}
                      onCheckedChange={setShowTitheContributorNames}
                    />
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1">
                    <Label htmlFor="show-yearly-budget-comparison" className="text-xs text-muted-foreground">Show yearly budget vs actual</Label>
                    <Switch
                      id="show-yearly-budget-comparison"
                      checked={showYearlyBudgetComparison}
                      onCheckedChange={setShowYearlyBudgetComparison}
                    />
                  </div>
                </div>
                </div>
                <Input
                  value={offeringHeading}
                  onChange={(e) => setOfferingHeading(e.target.value)}
                  placeholder="e.g. TITHES & OFFERING"
                />
              </div>
              {offeringData && offeringData.entries.length > 0 ? (
                <div className="rounded-lg border border-border bg-secondary/10 p-3 space-y-1.5">
                  {/* Tithe contributors: name + amount */}
                  {(offeringData.titheContributions.length > 0 || offeringData.tithesTotal > 0) && (
                    <div>
                      {showTitheContributorNames && offeringData.titheContributions.length > 0 && (
                        <>
                          <div className="space-y-0.5">
                            {offeringData.titheContributions.map((tc, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">{tc.name}</span>
                                <span className="font-medium text-foreground">${tc.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      <div className={cn("flex items-center justify-between text-sm font-bold", showTitheContributorNames && offeringData.titheContributions.length > 0 ? "pt-1 mt-1 border-t border-border/50" : "") }>
                        <span className="text-foreground">Tithes Total</span>
                        <span className="font-normal text-foreground">${offeringData.tithesTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}

                  {/* Offerings */}
                  {offeringData.offeringEntries.length > 0 && (
                    <div className="pt-1.5 mt-1 border-t border-border/50">
                      {offeringData.offeringEntries.map((e) => (
                        <div key={e.category} className="flex items-center justify-between text-sm">
                          <span className={cn("text-muted-foreground", e.category.trim().toLowerCase() === "offerings" ? "font-bold text-foreground" : "")}>{e.category}</span>
                          <span className="font-medium text-foreground">${e.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Separator className="my-1" />
                  <div className="flex items-center justify-between text-sm font-bold">
                    <span className="text-foreground">Total Contributions</span>
                    <span className="text-primary">${offeringData.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>

                  {showYearlyBudgetComparison && yearlyBudgetComparison && (
                    <div className="pt-1.5 mt-1 border-t border-border/50 space-y-0.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Yearly Budget</span>
                        <span className="font-medium text-foreground">${yearlyBudgetComparison.budgetTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Yearly Actual</span>
                        <span className="font-medium text-foreground">${yearlyBudgetComparison.actualTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-2">
                    <Textarea
                      id="offering-notes"
                      value={offeringNotes}
                      onChange={(event) => setOfferingNotes(event.target.value)}
                      className="text-sm min-h-[72px]"
                      placeholder="Add notes for the offering summary..."
                      rows={3}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No finance records found for {offeringDate}.</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
                  Close
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTemplateNameDialogOpen(true)}
                  disabled={saveAsTemplateMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saveAsTemplateMutation.isPending ? "Saving Template..." : "Save as Template"}
                </Button>
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  {saveMutation.isPending ? "Saving..." : editingTemplate ? "Update" : "Save Bulletin"}
                </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ===== TEMPLATE NAME DIALOG ===== */}
      <Dialog open={templateNameDialogOpen} onOpenChange={setTemplateNameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Save as Template</h3>
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Sunday Main Service Template"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && templateName.trim()) {
                    setTemplateNameDialogOpen(false);
                    saveAsTemplateMutation.mutate();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTemplateNameDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setTemplateNameDialogOpen(false);
                  saveAsTemplateMutation.mutate();
                }}
                disabled={!templateName.trim()}
              >
                <Save className="w-4 h-4 mr-2" /> Save Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== PREVIEW DIALOG ===== */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && setPreviewOpen(false)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto [&>button]:hidden">
          {previewTemplate && (
            <div className="space-y-4 py-2">
              {/* Layout and Font Size Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-border">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                  <Label className="text-xs">Layout:</Label>
                  <Select value={pageLayout} onValueChange={(v) => setPageLayout(v as PageLayout)}>
                    <SelectTrigger className="w-36 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2-page">2-Page</SelectItem>
                      <SelectItem value="4-page">4-Page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Font Size:</Label>
                  <Select value={fontSize.toString()} onValueChange={(v) => setFontSize(parseInt(v))}>
                    <SelectTrigger className="w-24 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 11, 12].map((size) => (
                        <SelectItem key={size} value={size.toString()}>{size}px</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={async () => {
                      if (!previewTemplate) return;
                      try {
                        setPreviewSharing(true);
                        await sharePDF(previewTemplate, { fontSize, pageLayout });
                      } catch (err: any) {
                        if (err?.name !== "AbortError") toast.error("Share failed");
                      } finally {
                        setPreviewSharing(false);
                      }
                    }}
                    disabled={previewSharing || previewDownloading}
                  >
                    {previewSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!previewTemplate) return;
                      try {
                        setPreviewDownloading(true);
                        await exportPDF(previewTemplate, { fontSize, pageLayout });
                      } finally {
                        setPreviewDownloading(false);
                      }
                    }}
                    disabled={previewSharing || previewDownloading}
                  >
                    {previewDownloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Download className="w-4 h-4 mr-2" /> PDF</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPreviewOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>

              {pageLayout === "4-page" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Page 1 - Front Cover - Right Side */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 1 (Front Cover - Right) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={1} />
                    </div>
                  </div>
                  {/* Page 2 - Back Cover - Left Side */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 2 (Back Cover - Left) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={2} />
                    </div>
                  </div>
                  {/* Page 3 - Inside Right */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 3 (Inside Right) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={3} />
                    </div>
                  </div>
                  {/* Page 4 - Inside Left */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 4 (Inside Left) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={4} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Page 1 - Right Side */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 1 (Right Side) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={1} />
                    </div>
                  </div>
                  {/* Page 2 - Left Side */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center mb-2">— Page 2 (Left Side) —</p>
                    <div className="rounded-lg border border-border bg-background p-6 aspect-[5.5/8.5] overflow-hidden">
                      <PreviewPanel template={previewTemplate} page={2} />
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bulletin?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
